import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/useAuthStore';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { PieceList } from './pages/PieceList';
import { PieceDetail } from './pages/PieceDetail';
import { Scanner } from './pages/Scanner';
import { Settings } from './pages/Settings';
import { getTabsForRole } from './domain';
import { SyncIndicator } from './components/UI/SyncIndicator';
import { RoughBodyList } from './pages/RoughBodyList';
import { RoughBodyDetail } from './pages/RoughBodyDetail';

// Auth Guard Component
const ProtectedRoute = ({ children }: { children?: React.ReactNode }) => {
  const { session, loading, userProfile } = useAuthStore();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Dynamic role check based on available tabs
  // If the user has any tabs (cutting, sewing, qc) they are allowed access.
  const allowedTabs = userProfile ? getTabsForRole(userProfile.role) : [];

  if (userProfile && allowedTabs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6 text-center">
        <div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-4">Your role ({userProfile.role}) does not have access to the mobile floor app.</p>
          <button
            onClick={() => useAuthStore.getState().signOut()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { initialize } = useAuthStore();

  useEffect(() => {
    initialize();
  }, []);

  return (
    <Router>
      <SyncIndicator />
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />

        <Route path="/sample/:sampleId/pieces" element={
          <ProtectedRoute>
            <PieceList />
          </ProtectedRoute>
        } />

        <Route path="/piece/:pieceId" element={
          <ProtectedRoute>
            <PieceDetail />
          </ProtectedRoute>
        } />

        <Route path="/scan" element={
          <ProtectedRoute>
            <Scanner />
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />

        <Route path="/rough-body" element={
          <ProtectedRoute>
            <RoughBodyList />
          </ProtectedRoute>
        } />

        <Route path="/rough-body/:qrCode" element={
          <ProtectedRoute>
            <RoughBodyDetail />
          </ProtectedRoute>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;